import AsyncSelect, {
  Option,
  type AsyncSelectBaseProps,
} from "@/components/AsyncSelect";
import { Module } from "@/constants";
import { SuccessFetchListResponse } from "@/services/api";
import { fetchContractorsList } from "@/services/roleBasedAssignmentsService";
import { ReadContractorVisitContractor } from "@/types/contractorVisitContractor";
import { forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface ContractorVisitsSelectProps extends AsyncSelectBaseProps {
  className?: string;
  inDialog?: boolean;
  queryParams?: {
    project_role_id: string;
    store_id: string;
    date: string;
  };
  disabled?: boolean;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, ContractorVisitsSelectProps>(
  function ContractorVisitsSelect(
    { value, inDialog, queryParams, disabled, defaultItem, ...props },
    ref,
  ) {
    const { t } = useTranslation();

    const { project_role_id, store_id, date } = queryParams ?? {};
    const enabled =
      project_role_id !== undefined &&
      store_id !== undefined &&
      date !== undefined &&
      date !== "";

    const selectData = useCallback(
      (data: SuccessFetchListResponse<ReadContractorVisitContractor>) => ({
        ...data,
        items: data.items.map((contractor) => ({
          ...contractor,
          id: contractor.contractor_id,
        })),
      }),
      [],
    );

    const queryFn = useCallback(async () => {
      const data = await fetchContractorsList({
        queryParams,
      });
      return selectData(data);
    }, [queryParams, selectData]);

    const renderName = useCallback(
      (contractor: ReadContractorVisitContractor & Option) =>
        contractor.contractor_name ?? contractor.name,
      [],
    );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={[
            Module.ContractorVisits,
            ...Object.values(queryParams ?? {}),
          ]}
          queryFn={queryFn}
          placeholder={t("contractorSelect.placeholder")}
          loadingLabel={t("contractorSelect.loadingContractors")}
          noResultsLabel={t("projectContractorVisits.noContractorsFound")}
          {...props}
          value={value ? value.toString() : undefined}
          ref={ref}
          enableFilter={false}
          inDialog={inDialog}
          disabled={disabled || !enabled}
          renderName={renderName}
          defaultItem={defaultItem}
        />
      </span>
    );
  },
);
